import { Timestamp } from 'firebase/firestore';

export interface User {
  id: string;
  name: string;
  email: string;
  image?: string;
  username?: string;
  usernameChangedAt?: Timestamp;
  createdAt: Timestamp;
}

// Zone Types
export type ZoneType = 'general' | 'server' | 'project' | 'guild' | 'feedback';

// Available topics/tags for zones
export const ZONE_TOPICS = [
  'adventure',
  'building',
  'combat',
  'creative',
  'minigames',
  'modding',
  'pvp',
  'roleplay',
  'survival',
  'trading',
  'tutorials',
  'news',
  'art',
  'memes',
  'lore',
  'technical',
] as const;

export type ZoneTopic = (typeof ZONE_TOPICS)[number];

// Zone type metadata
export const ZONE_TYPE_INFO: Record<ZoneType, { label: string; emoji: string; color: string }> = {
  general: { label: 'General', emoji: '💬', color: '#6B7280' },
  server: { label: 'Server', emoji: '🟢', color: '#10B981' },
  project: { label: 'Project', emoji: '📦', color: '#8B5CF6' },
  guild: { label: 'Guild', emoji: '🛡️', color: '#F59E0B' },
  feedback: { label: 'Feedback', emoji: '🛠️', color: '#3B82F6' },
};

// Type-specific fields for Server zones
export interface ServerFields {
  serverIp?: string;
  serverPort?: string;
  region?: 'na' | 'eu' | 'asia' | 'oceania' | 'sa';
  isOnline?: boolean;
  playerCount?: number;
  maxPlayers?: number;
  modsUsed?: string[];
}

// Type-specific fields for Project/Mod zones
export interface ProjectFields {
  version?: string;
  downloadUrl?: string;
  repositoryUrl?: string;
  compatibility?: string;
}

// Type-specific fields for Guild zones
export interface GuildFields {
  isRecruiting?: boolean;
  discordUrl?: string;
  memberRoster?: string[];
}

// Type-specific fields for Feedback zones
export interface FeedbackFields {
  categories?: string[];
}

export interface Group {
  id: string;
  name: string;
  description?: string;
  image?: string;
  banner?: string;
  membersCount?: number;
  createdBy?: string;
  createdAt: Timestamp;
  // Zone classification
  type: ZoneType;
  topics?: ZoneTopic[];
  // Type-specific fields
  serverFields?: ServerFields;
  projectFields?: ProjectFields;
  guildFields?: GuildFields;
  feedbackFields?: FeedbackFields;
}

export interface NewGroup {
  name: string;
  description?: string;
  image?: string;
  type: ZoneType;
  topics?: ZoneTopic[];
  // Type-specific fields
  serverFields?: ServerFields;
  projectFields?: ProjectFields;
  guildFields?: GuildFields;
  feedbackFields?: FeedbackFields;
}

export interface Post {
  id: string;
  title: string;
  description?: string;
  image?: string; // Legacy single image (backward compatibility)
  images?: string[]; // Multiple images (up to 10)
  video?: string; // Video URL (mutually exclusive with images)
  link?: string; // YouTube, Twitter/X, or other embeddable link
  groupId: string;
  userId: string;
  createdAt: Timestamp;
  // Aggregated fields (computed at query time)
  group?: Group;
  user?: User;
  upvotes?: number; // Count of upvotes (value = 1)
  downvotes?: number; // Count of downvotes (value = -1)
  upvotesSum?: number; // Net score (upvotes - downvotes)
  commentsCount?: number;
}

export interface Comment {
  id: string;
  comment: string;
  postId: string;
  userId: string;
  parentId?: string;
  createdAt: Timestamp;
  // Aggregated fields
  user?: User;
  replies?: Comment[];
}

export interface Upvote {
  id: string; // Format: `${postId}_${userId}`
  postId: string;
  userId: string;
  value: 1 | -1;
  createdAt: Timestamp;
}

// For creating new documents
export interface NewPost {
  title: string;
  description?: string;
  images?: string[]; // Multiple images (up to 10)
  video?: string; // Video URL (mutually exclusive with images)
  link?: string; // YouTube, Twitter/X, or other embeddable link
  groupId: string;
}

export interface NewComment {
  comment: string;
  postId: string;
  parentId?: string;
}

// CMS Types
export type CMSPageStatus = 'draft' | 'published';
export type CMSPageCategory = 'info' | 'legal' | 'help' | 'zone';

// Supported languages for CMS content
export type LanguageCode = 'en' | 'es';

export const SUPPORTED_LANGUAGES: readonly { code: LanguageCode; name: string; nativeName: string }[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
] as const;

export interface CMSPageSEO {
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string[];
  useFeatureImage?: boolean;
  ogImage?: string;
  canonicalUrl?: string;
  noIndex?: boolean;
}

export interface CMSPage {
  id: string;

  // Localization fields
  baseSlug: string;           // Shared identifier across translations (e.g., "about-us")
  language: LanguageCode;     // Language code: 'en' | 'es'
  isDefaultLanguage: boolean; // True for the original/source language version

  // Localizable content fields
  slug: string;               // Can be localized (e.g., "sobre-nosotros" for Spanish)
  urlPath?: string;           // URL path, can include language prefix (e.g., "/es/sobre-nosotros")
  title: string;
  content?: string;           // Legacy JSON content (optional)
  contentHtml: string;        // Primary HTML content from WYSIWYG editor

  // Shared fields (typically same across translations)
  status: CMSPageStatus;
  order: number;
  category: CMSPageCategory;
  featuredImage?: string;
  zoneId?: string;

  // SEO fields (fully localizable)
  seo?: CMSPageSEO;

  // Metadata
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
}

// Rich text content node types (for JSON parsing)
export type RichTextNodeType =
  | 'doc'
  | 'paragraph'
  | 'heading'
  | 'text'
  | 'bold'
  | 'italic'
  | 'link'
  | 'image'
  | 'video'
  | 'table'
  | 'tableRow'
  | 'tableCell'
  | 'bulletList'
  | 'orderedList'
  | 'listItem'
  | 'blockquote'
  | 'codeBlock'
  | 'horizontalRule';

export interface RichTextNode {
  type: RichTextNodeType;
  attrs?: Record<string, unknown>;
  content?: RichTextNode[];
  text?: string;
  marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
}
